import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  Cpu,
  GeoAlt,
  Gear,
  XLg,
} from "react-bootstrap-icons";
import { Alert, Spinner } from "react-bootstrap";
import { fetchDeviceTelemetryLatest, getDevice } from "../../services/api";
import DeviceInfoSection from "./sections/DeviceInfoSection";
import AlertsSection from "./sections/AlertsSection";
import DeviceSettingsSection from "./sections/DeviceSettingsSection";
import LocationSection from "./sections/LocationSection";
import styles from "./DeviceSettings.module.css";
import ViewOnlyWrapper from "../ViewOnlyWrapper";
import { PERMISSIONS } from "../../lib/utils/permissions";
import { usePermissions } from "../../Hooks/usePermission";
const tabs = [
  { id: "info", label: "Device Info", icon: Cpu },
  { id: "location", label: "Location", icon: GeoAlt },
  { id: "alerts", label: "Alerts", icon: Bell },
  { id: "settings", label: "Device Settings", icon: Gear },
];

const DEFAULT_TELEMETRY = {
  external_device_id: "",
  as_of: "",
  device: { model: "-", imei: "-", sim_number: "-" },
  status: {
    online: false,
    last_online_time: null,
    battery_level: null,
    in_charging: false,
    signal_strength: null,
    gps: false,
    device_status: "",
    motion_detected: null,
    fall_detected: null,
    cellular_mode: "",
    last_location: "",
  },
  location: [],
  alerts: [],
  settings: {
    address: "-",
    full_name: "Unknown User",
    gender: "-",
    age: null,
    ringtone_volume: 0,
    microphone_volume: 0,
    speaker_volume: 0,
    voice_prompt_volume: 0,
    no_motion_alert: { duration: 0, enable: false, dial: false },
    over_speed_alert: { enable: false, speed_threshold: 0 },
    tilt_alert: { time_threshold: 0, enable: false, angle_threshold: 0, dial: false },
    falldown_alert: { sensitive_level: 0, enable: false, always_on: false, dial: false },
    motion_alert: { duration: 0, enable: false, cooldown: 0, dial: false },
    time_interval: {
      heartbeat_enable: false,
      report_interval: 0,
      lazy_report_interval: 0,
      heartbeat_interval: 0,
      work_mode: 0,
    },
    geo_fence_labels: {},
    contact_numbers: {},
    geo_fences: {},
  },
};

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toOptionalNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveSimNumber(raw, fallbackSimNumber = "") {
  const candidates = [
    raw?.device?.sim_number,
    raw?.device?.msisdn,
    raw?.device?.simNumber,
    raw?.sim?.msisdn,
    raw?.sim?.sim_number,
    raw?.currentSim?.msisdn,
    raw?.current_sim?.msisdn,
    fallbackSimNumber,
  ];
  const value = candidates.find((v) => v != null && String(v).trim() !== "" && String(v).trim() !== "-");
  return value != null ? String(value).trim() : "-";
}

function normalizeTelemetry(raw, fallbackFullName = "", fallbackSimNumber = "") {
  if (!raw || typeof raw !== "object") {
    return {
      ...DEFAULT_TELEMETRY,
      device: {
        ...DEFAULT_TELEMETRY.device,
        sim_number: resolveSimNumber(null, fallbackSimNumber),
      },
      settings: {
        ...DEFAULT_TELEMETRY.settings,
        full_name: fallbackFullName || DEFAULT_TELEMETRY.settings.full_name,
      },
    };
  }

  const status = raw.status || {};
  const settings = raw.settings || {};
  const noMotion = settings.no_motion_alert || {};
  const overSpeed = settings.over_speed_alert || {};
  const tilt = settings.tilt_alert || {};
  const fallDown = settings.falldown_alert || {};
  const motion = settings.motion_alert || {};
  const interval = settings.time_interval || {};

  return {
    external_device_id: raw.external_device_id || "",
    as_of: raw.as_of || "",
    device: {
      model: raw.device?.model || "-",
      imei: raw.device?.imei || "-",
      sim_number: resolveSimNumber(raw, fallbackSimNumber),
    },
    status: {
      online: Boolean(status.online),
      last_online_time: status.last_online_time || null,
      battery_level: toOptionalNumber(status.battery_level),
      in_charging: Boolean(status.in_charging),
      signal_strength: toOptionalNumber(status.signal_strength),
      gps: Boolean(status.gps),
      device_status:
        status.device_status
        || (status.online === true ? "Online" : status.online === false ? "Offline" : ""),
      motion_detected:
        status.motion_detected == null
          ? (status.motion == null ? null : Boolean(status.motion))
          : Boolean(status.motion_detected),
      fall_detected:
        status.fall_detected == null
          ? (status.fall_detect == null ? null : Boolean(status.fall_detect))
          : Boolean(status.fall_detected),
      cellular_mode:
        status.cellular_mode || status.network_mode || status.network || "",
      last_location:
        status.last_location || status.last_known_location || "",
    },
    location: Array.isArray(raw.location) ? raw.location : [],
    alerts: Array.isArray(raw.alerts) ? raw.alerts : [],
    settings: {
      address: settings.address || "-",
      full_name: settings.full_name || fallbackFullName,
      gender: settings.gender || "-",
      age:
        settings.age == null || settings.age === ""
          ? null
          : toNumber(settings.age, null),
      ringtone_volume: toNumber(settings.ringtone_volume),
      microphone_volume: toNumber(settings.microphone_volume),
      speaker_volume: toNumber(settings.speaker_volume),
      voice_prompt_volume: toNumber(settings.voice_prompt_volume),
      no_motion_alert: {
        duration: toNumber(noMotion.duration),
        enable: Boolean(noMotion.enable),
        dial: Boolean(noMotion.dial),
      },
      over_speed_alert: {
        enable: Boolean(overSpeed.enable),
        speed_threshold: toNumber(overSpeed.speed_threshold),
      },
      tilt_alert: {
        time_threshold: toNumber(tilt.time_threshold),
        enable: Boolean(tilt.enable),
        angle_threshold: toNumber(tilt.angle_threshold),
        dial: Boolean(tilt.dial),
      },
      falldown_alert: {
        sensitive_level: toNumber(fallDown.sensitive_level),
        enable: Boolean(fallDown.enable),
        always_on: Boolean(fallDown.always_on),
        dial: Boolean(fallDown.dial),
      },
      motion_alert: {
        duration: toNumber(motion.duration),
        enable: Boolean(motion.enable),
        cooldown: toNumber(motion.cooldown),
        dial: Boolean(motion.dial),
      },
      time_interval: {
        heartbeat_enable: Boolean(interval.heartbeat_enable),
        report_interval: toNumber(interval.report_interval),
        lazy_report_interval: toNumber(interval.lazy_report_interval),
        heartbeat_interval: toNumber(interval.heartbeat_interval),
        work_mode: toNumber(interval.work_mode),
      },
      geo_fence_labels: settings.geo_fence_labels || {},
      contact_numbers: settings.contact_numbers || {},
      geo_fences: settings.geo_fences || {},
    },
  };
}

function isAssignedAccessMode(mode) {
  return String(mode || "").trim().toUpperCase() === "ASSIGNED";
}

export default function DeviceSettings({
  deviceId,
  fallbackFullName = "",
  fallbackSimNumber = "",
  caseManagerAccessMode = null,
  caseManagerName = "",
  onClose,
}) {
  const [activeTab, setActiveTab] = useState("info");
  const [telemetry, setTelemetry] = useState(DEFAULT_TELEMETRY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const { can } = usePermissions();
  const canViewDevice = can(PERMISSIONS.DEVICE_READ, userRoleCode);
  const canViewAlerts = can(PERMISSIONS.ALERTS_VIEW, userRoleCode);
  const canViewSettings = can(PERMISSIONS.DEVICE_SETTINGS_VIEW, userRoleCode);

  const visibleTabs = useMemo(
    () =>
      tabs.filter((tab) => {
        if (tab.id === "alerts") return canViewAlerts;
        if (tab.id === "settings") return canViewSettings;
        if (tab.id === "location") return canViewDevice;
        return canViewDevice;
      }),
    [canViewAlerts, canViewSettings, canViewDevice],
  );

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || "info");
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    let cancelled = false;
    async function resolveSimNumberFromDevice(id) {
      try {
        const deviceResponse = await getDevice(id);
        const deviceData = deviceResponse?.data?.data;
        return (
          deviceData?.currentSim?.msisdn
          || deviceData?.current_sim?.msisdn
          || deviceData?.msisdn
          || ""
        );
      } catch {
        return "";
      }
    }

    async function loadTelemetry() {
      if (!deviceId) {
        setTelemetry(normalizeTelemetry(null, fallbackFullName, fallbackSimNumber));
        return;
      }
      setLoading(true);
      setError("");
      try {
        const needsSimLookup = !fallbackSimNumber;
        const [response, simFromDevice] = await Promise.all([
          fetchDeviceTelemetryLatest(deviceId),
          needsSimLookup ? resolveSimNumberFromDevice(deviceId) : Promise.resolve(""),
        ]);
        const payload = response?.data?.data;
        if (!cancelled) {
          setTelemetry(
            normalizeTelemetry(
              payload,
              fallbackFullName,
              fallbackSimNumber || simFromDevice,
            ),
          );
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message || err?.message || "Failed to load device telemetry.",
        );
        const simFromDevice = fallbackSimNumber
          || await resolveSimNumberFromDevice(deviceId);
        if (!cancelled) {
          setTelemetry(normalizeTelemetry(null, fallbackFullName, simFromDevice));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTelemetry();
    return () => {
      cancelled = true;
    };
  }, [deviceId, fallbackFullName, fallbackSimNumber]);

  const alertCount = telemetry.alerts.length;
  const showCareTeam = isAssignedAccessMode(caseManagerAccessMode);
  const careTeamName = String(caseManagerName || "").trim();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.headerName}>{telemetry.settings.full_name}</h1>
          <p className={styles.headerMeta}>
            Device ID: {telemetry.external_device_id || "-"} &middot; IMEI: {telemetry.device.imei}
            {" "}&middot; SIM Number: {telemetry.device.sim_number}
          </p>
        </div>
        <div className={styles.headerRight}>
          {showCareTeam ? (
            <div className={styles.careTeam}>
              <p className={styles.careTeamHeading}>Care Team</p>
              <p className={styles.careTeamName}>{careTeamName || "Not assigned"}</p>
            </div>
          ) : null}
          {onClose ? (
            <button type="button" onClick={onClose} className={styles.closeButton} aria-label="Close">
              <XLg size={18} />
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.tabBar} role="tablist" aria-label="Device tabs">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const showBadge = tab.id === "alerts" && alertCount > 0;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.id)}
              className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""}`}
            >
              <Icon size={14} />
              <span>{tab.label}</span>
              {showBadge ? <span className={styles.alertBadge}>{alertCount}</span> : null}
            </button>
          );
        })}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingState}>
            <Spinner size="sm" animation="border" />
            <span>Loading telemetry...</span>
          </div>
        ) : null}
        {!!error ? (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {activeTab === "info" && canViewDevice ? (
          <ViewOnlyWrapper viewOnly={true}>
          <DeviceInfoSection
            device={telemetry.device}
            status={telemetry.status}
            settings={telemetry.settings}
            location={telemetry.location}
          />
          </ViewOnlyWrapper>
        ) : null}
        {activeTab === "location" && canViewDevice ? <LocationSection location={telemetry.location} /> : null}
        {activeTab === "alerts" && canViewAlerts ? <ViewOnlyWrapper viewOnly={true}> <AlertsSection alerts={telemetry.alerts} /> </ViewOnlyWrapper> : null}
        {activeTab === "settings" && canViewSettings ? (
          <ViewOnlyWrapper viewOnly={true}> <DeviceSettingsSection settings={telemetry.settings} /> </ViewOnlyWrapper>
        ) : null}
        {visibleTabs.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            You do not have permission to view device details.
          </Alert>
        ) : null}
      </div>
    </div>
  );
}
